import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { TourProvider } from './context/TourContext';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SignupPage from './pages/SignupPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SupportPage from './pages/SupportPage';
import DashboardPage from './pages/DashboardPage';
import MyBookingsPage from './pages/MyBookingsPage';
import PropertyPage from './pages/PropertyPage';
import GuidePage from './pages/GuidePage';
import DocumentLibraryPage from './pages/DocumentLibraryPage';
import UsersPage from './pages/admin/UsersPage';
import PendingBookingsPage from './pages/admin/PendingBookingsPage';
import SettingsPage from './pages/admin/SettingsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import GuideListPage from './pages/admin/GuideListPage';
import GuideEditorPage from './pages/admin/GuideEditorPage';
import ImportPage from './pages/admin/ImportPage';
import BookingsPage from './pages/admin/BookingsPage';
import UsageStatsPage from './pages/admin/UsageStatsPage';
import AnnouncementsPage from './pages/admin/AnnouncementsPage';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

export default function App() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/support" element={<SupportPage />} />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <TourProvider>
              <Layout />
            </TourProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/my-bookings" element={<MyBookingsPage />} />
        <Route path="/property" element={<PropertyPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/guide/:slug" element={<GuidePage />} />
        <Route path="/documents" element={<DocumentLibraryPage />} />

        {/* Admin routes */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requiredRole="admin">
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <ProtectedRoute requiredRole="owner">
              <PendingBookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requiredRole="admin">
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-log"
          element={
            <ProtectedRoute requiredRole="admin">
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/guides"
          element={
            <ProtectedRoute requiredRole="admin">
              <GuideListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/guides/new"
          element={
            <ProtectedRoute requiredRole="admin">
              <GuideEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/guides/:id/edit"
          element={
            <ProtectedRoute requiredRole="admin">
              <GuideEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/import"
          element={
            <ProtectedRoute requiredRole="owner">
              <ImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/manage-bookings"
          element={
            <ProtectedRoute requiredRole="owner">
              <BookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/usage-stats"
          element={
            <ProtectedRoute requiredRole="admin">
              <UsageStatsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute requiredRole="admin">
              <AnnouncementsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
